import { ReactNode, useEffect, useState } from "react";
import type { LanguagePreference } from "./language";
import { useDispatch } from "react-redux";
import { useAuthState } from "react-firebase-hooks/auth";
import Loader from "../components/InsertElements/Loader";
import { useFirebase } from "../context/FirebaseContext";
import { isDemoUserId } from "../utility/demoSession";
import {
  applyDocumentLanguage,
  getInitialLanguagePreference,
  getSupportedLanguage,
  LANGUAGE_SOURCES,
  setStoredLanguage
} from "./language";
import { persistLanguageInProfile } from "./useLanguagePreference";

const ANONYMOUS_LANGUAGE_KEY = "anonymous";

function LanguageBootstrap({ children }: { children: ReactNode }) {
  const { auth, firestore } = useFirebase();
  const [user, authLoading] = useAuthState(auth);
  const dispatch = useDispatch();
  const authKey = authLoading
    ? null
    : user?.uid || ANONYMOUS_LANGUAGE_KEY;
  const [resolvedAuthKey, setResolvedAuthKey] = useState<string | null>(null);

  useEffect(() => {
    if (!authKey) return undefined;

    let active = true;

    const applyPreference = (preference: LanguagePreference) => {
      if (!active) return;
      const language = applyDocumentLanguage(preference.language);
      dispatch({ type: "setLanguage", payload: language });
    };

    const resolveLanguage = async () => {
      const localPreference = getInitialLanguagePreference();

      // Never hold the whole application behind a Firestore round trip. The
      // cached/browser preference is good enough for the first paint; a
      // profile preference can be reconciled in the background afterwards.
      applyPreference(localPreference);
      if (active) setResolvedAuthKey(authKey);

      if (!user?.uid) {
        return;
      }

      if (localPreference.source === LANGUAGE_SOURCES.MANUAL) {
        if (!isDemoUserId(user.uid)) {
          void persistLanguageInProfile({
            firestore,
            userId: user.uid,
            language: localPreference.language
          }).catch((error: unknown) => {
            console.error("Не удалось синхронизировать язык профиля:", error);
          });
        }
        return;
      }

      try {
        const snapshot = await firestore
          .collection("users")
          .doc(user.uid)
          .get({ source: "server" });
        const profileLanguage = getSupportedLanguage(
          snapshot.exists ? snapshot.data()?.language : null
        );

        if (profileLanguage) {
          const profilePreference = setStoredLanguage(
            profileLanguage,
            LANGUAGE_SOURCES.PROFILE
          );
          applyPreference(profilePreference);
        }
      } catch (error) {
        // A profile outage must not block navigation or clear local state.
        console.warn("Не удалось загрузить язык профиля, используется локальный:", error);
      }

    };

    void resolveLanguage();
    return () => {
      active = false;
    };
  }, [authKey, dispatch, firestore, user?.uid]);

  if (!authKey || resolvedAuthKey !== authKey) {
    const loadingLanguage = getInitialLanguagePreference().language;
    return (
      <div
        className="page-loader"
        role="status"
        aria-label={loadingLanguage === "ru" ? "Загрузка" : "Loading"}
      >
        <Loader />
      </div>
    );
  }

  return <>{children}</>;
}

export default LanguageBootstrap;
